import React, { ComponentProps, forwardRef } from 'react'
import { ActivityIndicator, ColorValue, View } from 'react-native'

import { styled } from 'nativewind'

import { Stack, XStack } from '../layout'
import Pressable from '../pressable'
import { BTN } from '../typography/Typography'

const ButtonFrame = styled(Pressable, 'rounded w-[148]', {
  variants: {
    intent: {
      primary: `bg-primary-100 hover:bg-primary-150 active:bg-primary-150`,
      error: `bg-error-100 hover:bg-error-150 active:bg-error-150`,
      success: `bg-success-100 hover:bg-success-150 active:bg-success-150`,
      warning: `bg-warning-100 hover:bg-warning-150 active:bg-warning-150`,
      'primary-outline': `bg-transparent hover:bg-primary-10 active:bg-primary-10 border border-primary-100 hover:border-primary-150 active:border-primary-150`,
    },
    outlined: {
      true: 'bg-transparent border',
    },
    fullWidth: {
      true: 'w-full',
    },
    textOnly: {
      true: 'bg-transparent border-none',
    },
    size: {
      small: 'py-1 px-2',
      medium: 'py-2 px-4',
      lg: 'py-3 px-4',
    },
    disable: {
      true: 'bg-grays-50 text-neutral-white100 opacity-60',
    },
    disabled: {
      true: true,
      false: false,
    },
  },
  defaultProps: {
    size: 'medium',
    intent: 'primary',
  },
  compoundVariants: [
    {
      intent: 'primary',
      outlined: true,
      className:
        'border-primary-100 hover:border-primary-150 active:border-primary-150 hover:border-primary-50 hover:bg-primary-10 active:bg-primary-10',
    },
    {
      intent: 'warning',
      outlined: true,
      className:
        'border-warning-100 hover:border-warning-150 active:border-warning-150',
    },
    {
      intent: 'success',
      outlined: true,
      className:
        'border-success-100 hover:border-success-150 active:border-success-150',
    },
    {
      intent: 'error',
      outlined: true,
      className:
        'border-error-100 hover:border-error-150 active:border-error-150',
    },
  ],
})

type ButtonFrameProps = ComponentProps<typeof ButtonFrame>

const ButtonText = styled(BTN, 'text-center flex-grow', {
  variants: {
    intent: {
      primary: '',
      error: '',
      success: '',
      warning: '',
    },
    outlined: {
      true: 'text-black',
      false: 'text-white',
    },
    textOnly: {
      true: 'text-black',
    },
    size: {
      small: 'font-[Graphie-SemiBold] text-xs leading-5',
    },
  },
  defaultProps: {
    size: 'medium',
    outlined: false,
  },
  compoundVariants: [
    {
      outlined: true,
      intent: 'primary',
      className: 'text-primary-100',
    },
    {
      outlined: true,
      intent: 'error',
      className: 'text-error-100',
    },
    {
      outlined: true,
      intent: 'warning',
      className: 'text-warning-100',
    },
    {
      outlined: true,
      intent: 'success',
      className: 'text-success-100',
    },
    {
      textOnly: true,
      intent: 'primary',
      className: 'text-primary-100',
    },
    {
      textOnly: true,
      intent: 'error',
      className: 'text-error-100',
    },
    {
      textOnly: true,
      intent: 'warning',
      className: 'text-warning-100',
    },
    {
      textOnly: true,
      intent: 'success',
      className: 'text-success-100',
    },
  ],
})
type ButtonTextProps = ComponentProps<typeof ButtonText>
type ButtonProps = ButtonFrameProps &
  ButtonTextProps & {
    iconRight?: React.ReactNode
    iconLeft?: React.ReactNode
    loading?: boolean
    disabled?: boolean
    loaderColor?: ColorValue | undefined
    loaderText?: string
  }

export const Button = forwardRef<View | TouchableOpacity, ButtonProps>(
  (
    {
      intent = 'primary',
      outlined,
      textOnly,
      fullWidth,
      size = 'medium',
      iconLeft,
      iconRight,
      disabled = false,
      loading = false,
      loaderColor = '#FFFFFF',
      loaderText = '',
      numberOfLines,
      ...props
    },
    ref
  ) => {
    return (
      <ButtonFrame
        intent={intent}
        outlined={outlined}
        textOnly={textOnly}
        size={size}
        fullWidth={fullWidth}
        disable={disabled}
        disabled={disabled}
        loaderText={loaderText}
        ref={ref}
        {...props}
      >
        {loading ? (
          <Stack
            className="py-[1]"
            style={{
              alignItems: 'center',
              justifyContent: 'center',
              flexDirection: 'row',
            }}
          >
            {loaderText && <BTN className="mr-2 text-white">{loaderText}</BTN>}
            <ActivityIndicator color={loaderColor} />
          </Stack>
        ) : (
          <XStack className="items-center">
            <Stack className="items-center">{iconLeft && iconLeft}</Stack>

            <ButtonText
              intent={intent}
              outlined={outlined}
              textOnly={textOnly}
              className={props.textStyle}
              size={size}
              numberOfLines={numberOfLines}
            >
              {props.children}
            </ButtonText>

            <Stack className="justify-center">{iconRight && iconRight}</Stack>
          </XStack>
        )}
      </ButtonFrame>
    )
  }
)
